import React, { useState, useEffect } from "react";
import { Popover, Input, Button, Popconfirm, message } from "antd";
import { MdAddCircle } from "react-icons/md";
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import Axios from "axios";

const VvSkuPopover = ({ productId, onSave, onCreateNew, currentVvSku, isEditing = false }) => {
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [vvSku, setVvSku] = useState("");
    const [loading, setLoading] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [removeLoading, setRemoveLoading] = useState(false);

    useEffect(() => {
        if (isEditing && currentVvSku) {
            setVvSku(currentVvSku);
        } else if (!isEditing) {
            setVvSku("");
        }
    }, [isEditing, currentVvSku, popoverVisible]);

    const handleVisibilityChange = (visible) => {
        setPopoverVisible(visible);
        if (!visible && isEditing && currentVvSku) {
            setVvSku(currentVvSku);
        } else if (!visible && !isEditing) {
            setVvSku("");
        }
    };

    const handleSave = async () => {
        if (removeLoading || createLoading) {
            return;
        }

        if (!vvSku.trim()) {
            message.error("VV SKU cannot be empty");
            return;
        }

        setLoading(true);
        try {
            if (!isEditing || vvSku !== currentVvSku) {
                const checkResponse = await Axios.post(`/api/v1/products/${productId}/check_vv_sku`, {
                    vv_sku: vvSku
                });

                if (checkResponse.data?.success === false) {
                    message.error(checkResponse.data?.message || "VV SKU already exists");
                    setLoading(false);
                    return;
                }
            }

            await Axios.put(`/api/v1/products/${productId}`, { vv_sku: vvSku });

            if (onSave) {
                onSave(productId, vvSku);
            }

            message.success(isEditing ? "VV SKU updated successfully" : "VV SKU saved successfully");
            if (!isEditing) {
                setVvSku("");
            }
            setPopoverVisible(false);
        } catch (error) {
            console.error("Error saving VV SKU:", error);
            if (error.response?.status === 409) {
                message.error("This VV SKU is already in use");
            } else {
                message.error(error.response?.data?.message || "Failed to save VV SKU");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = async () => {
        if (loading || removeLoading) {
            return;
        }

        setCreateLoading(true);
        try {
            await Axios.post(`/api/v1/products/${productId}/create_vv_product`);

            if (onCreateNew) {
                onCreateNew(productId);
            }

            message.success("VV product created successfully");
            setPopoverVisible(false);
        } catch (error) {
            console.error("Error creating VV product:", error);
            message.error(error.response?.data?.message || "Failed to create VV product");
        } finally {
            setCreateLoading(false);
        }
    };

    const handleRemove = async () => {
        if (loading || createLoading || removeLoading) {
            return;
        }

        setRemoveLoading(true);
        try {
            await Axios.put(`/api/v1/products/${productId}`, { vv_sku: null });

            if (onSave) {
                onSave(productId, null);
            }

            message.success("VV SKU removed successfully");
            setVvSku("");
            setPopoverVisible(false);
        } catch (error) {
            console.error("Error removing VV SKU:", error);
            message.error(error.response?.data?.message || "Failed to remove VV SKU");
        } finally {
            setRemoveLoading(false);
        }
    };

    const content = (
        <div style={{ width: 250 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                    <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>VV SKU</label>
                    <Input
                        value={vvSku}
                        onChange={(e) => setVvSku(e.target.value)}
                        placeholder="Enter VV SKU"
                        style={{ width: "100%" }}
                        onPressEnter={handleSave}
                        disabled={removeLoading || createLoading}
                    />
                </div>
                <Button
                    type="primary"
                    onClick={handleSave}
                    loading={loading}
                    disabled={createLoading || removeLoading}
                    style={{ marginLeft: 8, marginTop: 24 }}
                >
                    Save
                </Button>
            </div>
            {isEditing && (
                <div style={{ marginBottom: 8 }}>
                    <Popconfirm
                        title="Remove VV SKU assignment?"
                        description="This will clear VV SKU from this product and may impact VV integrations."
                        onConfirm={handleRemove}
                        okText="Remove"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true, loading: removeLoading }}
                        disabled={loading || createLoading || removeLoading}
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            style={{ padding: 0 }}
                            disabled={loading || createLoading || removeLoading}
                        >
                            Remove VV SKU
                        </Button>
                    </Popconfirm>
                </div>
            )}
            {!isEditing && (
                <div>
                    <span>Missing? </span>
                    <Button
                        type="link"
                        onClick={handleCreateNew}
                        loading={createLoading}
                        disabled={loading || removeLoading}
                        style={{ padding: 0 }}
                    >
                        Create new VV product
                    </Button>
                </div>
            )}
        </div>
    );

    return (
        <Popover
            content={content}
            title={isEditing ? "Edit VV SKU" : "Add VV SKU"}
            visible={popoverVisible}
            onVisibleChange={handleVisibilityChange}
            trigger="click"
            placement="right"
        >
            {isEditing ? (
                <EditOutlined
                    style={{ color: "#03B0F7", cursor: "pointer", fontSize: "14px" }}
                />
            ) : (
                <MdAddCircle
                    color="#03B0F7"
                    size={16}
                    style={{ cursor: "pointer" }}
                />
            )}
        </Popover>
    );
};

export default VvSkuPopover;